/**
 * preferences.js  —  Unified user-view-preferences Vuex module
 *
 * Single source of truth for every UI preference. Persisted to:
 *   localStorage key: "switchq:userPreferences"
 *
 * Migrates automatically from legacy keys:
 *   "dark"  →  preferences.theme
 *   "switchq:viewPreferences".devices  →  preferences.pages.devices.viewMode
 *   "groups_view_mode"  →  preferences.pages.groups.viewMode
 *   "homePage"          →  preferences.landingPage
 *
 * Backend sync: POST /user/preferences (debounced 2 s, silent on failure).
 *               GET /user/preferences (fetched on sign-in, merged if newer).
 */

import axios from "@/plugins/axios";

const STORAGE_KEY = "switchq:userPreferences";
const SCHEMA_VERSION = 1;
const SYNC_DEBOUNCE_MS = 2000;

// ── Default dashboard section order & visibility ─────────────────────────────
export const DEFAULT_DASHBOARD_COMPONENTS = [
  { id: "criticalEvents", visible: true },
  { id: "kpis", visible: true },
  { id: "aiInsights", visible: true },
  { id: "recommendedActions", visible: true },
  { id: "devicesStatus", visible: true },
  { id: "whatChanged", visible: true },
  { id: "problematicDevices", visible: true },
  { id: "activeIssues", visible: true },
  { id: "sensorOverview", visible: true },
  { id: "map", visible: true },
  { id: "groupsHealth", visible: true },
  { id: "alertsByType", visible: true },
];

// ── Default preference object ─────────────────────────────────────────────────
function defaultPreferences() {
  return {
    version: SCHEMA_VERSION,
    theme: "light", // "light" | "dark"
    landingPage: "dashboard", // "dashboard" | "devices" | "groups" | "map"
    pages: {
      devices: { viewMode: "table" }, // "table" | "cards"
      groups: { viewMode: "cards" }, // "list" | "cards" | "charts"
      map: { panelVisible: true },
    },
    dashboard: {
      components: DEFAULT_DASHBOARD_COMPONENTS.map((c) => ({ ...c })),
    },
    updatedAt: null,
  };
}

// ── Storage helpers ───────────────────────────────────────────────────────────
function saveToLocalStorage(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (_) {
    // storage unavailable – ignore
  }
}

function loadFromLocalStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

// ── Migration from legacy keys ────────────────────────────────────────────────
function migrateLegacyKeys(prefs) {
  let changed = false;

  // theme  ←  "dark"
  const darkRaw = localStorage.getItem("dark");
  if (darkRaw !== null && !prefs._migratedDark) {
    prefs.theme = darkRaw === "1" ? "dark" : "light";
    prefs._migratedDark = true;
    changed = true;
  }

  // pages.devices.viewMode  ←  switchq:viewPreferences.devices
  if (!prefs._migratedDevicesView) {
    try {
      const legacyPrefs = JSON.parse(
        localStorage.getItem("switchq:viewPreferences") || "{}"
      );
      if (legacyPrefs.devices) {
        prefs.pages.devices.viewMode =
          legacyPrefs.devices === "card" || legacyPrefs.devices === "cards"
            ? "cards"
            : "table";
      }
    } catch (_) {
      /* ignore */
    }
    prefs._migratedDevicesView = true;
    changed = true;
  }

  // pages.groups.viewMode  ←  groups_view_mode
  if (!prefs._migratedGroupsView) {
    const gvm = localStorage.getItem("groups_view_mode");
    if (gvm && ["list", "cards", "charts"].includes(gvm)) {
      prefs.pages.groups.viewMode = gvm;
    }
    prefs._migratedGroupsView = true;
    changed = true;
  }

  // landingPage  ←  homePage
  if (!prefs._migratedHomePage) {
    const hp = localStorage.getItem("homePage");
    const validPages = ["dashboard", "devices", "groups", "map"];
    if (hp) {
      // old value "mainDash" → "dashboard"
      const mapped =
        hp === "mainDash" || hp === "dashboard"
          ? "dashboard"
          : validPages.includes(hp)
          ? hp
          : null;
      if (mapped) prefs.landingPage = mapped;
    }
    prefs._migratedHomePage = true;
    changed = true;
  }

  return changed;
}

// ── Validate and repair a loaded preferences object ───────────────────────────
function validate(prefs) {
  if (!prefs || typeof prefs !== "object") return defaultPreferences();
  const def = defaultPreferences();

  // Ensure nested structures
  if (!prefs.pages || typeof prefs.pages !== "object") prefs.pages = def.pages;
  if (!prefs.pages.devices) prefs.pages.devices = def.pages.devices;
  if (!prefs.pages.groups) prefs.pages.groups = def.pages.groups;
  if (!prefs.pages.map) prefs.pages.map = def.pages.map;
  if (!prefs.dashboard || typeof prefs.dashboard !== "object") {
    prefs.dashboard = def.dashboard;
  }
  if (
    !Array.isArray(prefs.dashboard.components) ||
    !prefs.dashboard.components.length
  ) {
    prefs.dashboard.components = def.dashboard.components;
  } else {
    // Merge in any new sections that don't exist yet
    const existingIds = new Set(prefs.dashboard.components.map((c) => c.id));
    for (const def_c of DEFAULT_DASHBOARD_COMPONENTS) {
      if (!existingIds.has(def_c.id)) {
        prefs.dashboard.components.push({ ...def_c });
      }
    }
  }

  // Validate scalar fields
  if (!["light", "dark"].includes(prefs.theme)) prefs.theme = def.theme;
  if (!["dashboard", "devices", "groups", "map"].includes(prefs.landingPage)) {
    prefs.landingPage = def.landingPage;
  }
  if (prefs.pages.devices.viewMode === "card") {
    prefs.pages.devices.viewMode = "cards";
  }
  if (!["table", "cards"].includes(prefs.pages.devices.viewMode)) {
    prefs.pages.devices.viewMode = def.pages.devices.viewMode;
  }
  if (!["list", "cards", "charts"].includes(prefs.pages.groups.viewMode)) {
    prefs.pages.groups.viewMode = def.pages.groups.viewMode;
  }

  return prefs;
}

// ── Debounce helper ───────────────────────────────────────────────────────────
let _syncTimer = null;
function debouncedSync(fn) {
  clearTimeout(_syncTimer);
  _syncTimer = setTimeout(fn, SYNC_DEBOUNCE_MS);
}

// ═══════════════════════════════════════════════════════════════════════════════
export const state = () => ({
  ...defaultPreferences(),
  syncing: false,
  syncError: null,
  hydrated: false,
});

export const mutations = {
  SET_PREFERENCES(state, prefs) {
    const v = validate({ ...prefs });
    Object.assign(state, v);
    state.hydrated = true;
  },
  SET_THEME(state, theme) {
    if (!["light", "dark"].includes(theme)) return;
    state.theme = theme;
    state.updatedAt = new Date().toISOString();
  },
  SET_LANDING_PAGE(state, page) {
    if (!["dashboard", "devices", "groups", "map"].includes(page)) return;
    state.landingPage = page;
    state.updatedAt = new Date().toISOString();
  },
  SET_PAGE_VIEW_MODE(state, { page, viewMode }) {
    if (!state.pages[page]) return;
    state.pages[page] = { ...state.pages[page], viewMode };
    state.updatedAt = new Date().toISOString();
  },
  SET_MAP_PANEL_VISIBLE(state, visible) {
    state.pages.map = { ...state.pages.map, panelVisible: !!visible };
    state.updatedAt = new Date().toISOString();
  },
  SET_DASHBOARD_COMPONENTS(state, components) {
    if (!Array.isArray(components)) return;
    state.dashboard = {
      ...state.dashboard,
      components: components.map((c) => ({ ...c })),
    };
    state.updatedAt = new Date().toISOString();
  },
  TOGGLE_DASHBOARD_COMPONENT(state, { id, visible }) {
    const idx = state.dashboard.components.findIndex((c) => c.id === id);
    if (idx === -1) return;
    const updated = state.dashboard.components.map((c, i) =>
      i === idx
        ? { ...c, visible: visible !== undefined ? !!visible : !c.visible }
        : c
    );
    state.dashboard = { ...state.dashboard, components: updated };
    state.updatedAt = new Date().toISOString();
  },
  MOVE_DASHBOARD_COMPONENT(state, { id, direction }) {
    const arr = [...state.dashboard.components];
    const idx = arr.findIndex((c) => c.id === id);
    if (idx === -1) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[newIdx];
    arr[newIdx] = tmp;
    state.dashboard = { ...state.dashboard, components: arr };
    state.updatedAt = new Date().toISOString();
  },
  SET_SYNCING(state, val) {
    state.syncing = !!val;
  },
  SET_SYNC_ERROR(state, err) {
    state.syncError = err;
  },
  RESET(state) {
    const def = defaultPreferences();
    Object.assign(state, def);
    state.hydrated = false;
  },
};

export const getters = {
  theme: (state) => state.theme,
  isDark: (state) => state.theme === "dark",
  landingPage: (state) => state.landingPage,
  devicesViewMode: (state) => state.pages.devices.viewMode,
  groupsViewMode: (state) => state.pages.groups.viewMode,
  mapPanelVisible: (state) => state.pages.map.panelVisible,
  dashboardComponents: (state) => state.dashboard.components,
  visibleDashboardComponents: (state) =>
    state.dashboard.components.filter((c) => c.visible),
  preferences: (state) => {
    const { syncing, syncError, hydrated, ...rest } = state; // eslint-disable-line no-unused-vars
    return rest;
  },
};

export const actions = {
  /**
   * Hydrate preferences from localStorage (with migration from legacy keys).
   * Call once at app startup (main.js) before the Vue instance is mounted.
   */
  hydrate({ commit, state: s }) {
    if (s.hydrated) return;

    let loaded = loadFromLocalStorage();
    const def = defaultPreferences();
    if (!loaded) {
      loaded = { ...def };
    }

    // Run migrations
    const changed = migrateLegacyKeys(loaded);

    // Validate
    const prefs = validate(loaded);

    commit("SET_PREFERENCES", prefs);

    // Persist back if anything was migrated / filled-in
    if (changed || !loadFromLocalStorage()) {
      saveToLocalStorage({ ...prefs });
    }
  },

  /**
   * Merge preferences coming directly from the user API response (e.g. GET /user
   * or POST /user/signin).  Replaces the local copy unconditionally because the
   * server value is considered authoritative at login / restore time.
   */
  mergeFromServer({ commit }, remotePrefs) {
    if (!remotePrefs || typeof remotePrefs !== "object") return;
    const merged = validate({ ...defaultPreferences(), ...remotePrefs });
    commit("SET_PREFERENCES", merged);
    saveToLocalStorage(merged);
  },

  /**
   * Fetch preferences from backend and merge if newer.
   */
  async fetchRemote({ commit, state: s }) {
    try {
      const res = await axios.get("/user/preferences");
      const remote = (res.data && (res.data.data || res.data)) || null;
      if (!remote || typeof remote !== "object") return;
      const remotePrefs = remote.preferences || remote;
      if (!remotePrefs || typeof remotePrefs !== "object") return;

      // Merge only if remote is newer or local has no timestamp
      const remoteAt = remotePrefs.updatedAt || remote.updated_at || null;
      const localAt = s.updatedAt;
      const remoteNewer =
        !localAt || (remoteAt && new Date(remoteAt) > new Date(localAt));

      if (remoteNewer) {
        const merged = validate({ ...defaultPreferences(), ...remotePrefs });
        commit("SET_PREFERENCES", merged);
        saveToLocalStorage(merged);
      }
    } catch (_) {
      // backend unavailable – rely on localStorage
    }
  },

  /**
   * Push current preferences to the backend (debounced, silent on failure).
   */
  syncRemote({ state: s, commit }) {
    debouncedSync(async () => {
      commit("SET_SYNCING", true);
      try {
        const { syncing, syncError, hydrated, ...prefs } = s; // eslint-disable-line no-unused-vars
        await axios.post("/user/preferences", { preferences: prefs });
        commit("SET_SYNC_ERROR", null);
      } catch (err) {
        commit(
          "SET_SYNC_ERROR",
          err && err.message ? err.message : "sync_failed"
        );
        // Do not throw – feature works without backend
      } finally {
        commit("SET_SYNCING", false);
      }
    });
  },

  /** Save theme preference */
  setTheme({ commit, dispatch }, theme) {
    commit("SET_THEME", theme);
    const prefs = { ...this.getters["preferences/preferences"], theme };
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Save landing page preference */
  setLandingPage({ commit, dispatch }, page) {
    commit("SET_LANDING_PAGE", page);
    const prefs = this.getters["preferences/preferences"];
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Save a page's view mode (page: "devices" | "groups") */
  setPageViewMode({ commit, dispatch }, { page, viewMode }) {
    commit("SET_PAGE_VIEW_MODE", { page, viewMode });
    const prefs = this.getters["preferences/preferences"];
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Save map sidebar visibility */
  setMapPanelVisible({ commit, dispatch }, visible) {
    commit("SET_MAP_PANEL_VISIBLE", visible);
    const prefs = this.getters["preferences/preferences"];
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Replace the full dashboard component list (for drag-and-drop reorder) */
  setDashboardComponents({ commit, dispatch }, components) {
    commit("SET_DASHBOARD_COMPONENTS", components);
    const prefs = this.getters["preferences/preferences"];
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Toggle one dashboard component's visibility */
  toggleDashboardComponent({ commit, dispatch }, { id, visible }) {
    commit("TOGGLE_DASHBOARD_COMPONENT", { id, visible });
    const prefs = this.getters["preferences/preferences"];
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Move a dashboard component up/down */
  moveDashboardComponent({ commit, dispatch }, { id, direction }) {
    commit("MOVE_DASHBOARD_COMPONENT", { id, direction });
    const prefs = this.getters["preferences/preferences"];
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Reset all preferences to defaults */
  resetPreferences({ commit, dispatch }) {
    commit("RESET");
    const prefs = defaultPreferences();
    saveToLocalStorage(prefs);
    dispatch("syncRemote");
  },

  /** Called on logout – clear the stored key */
  logOut() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  },
};

export const namespaced = true;
